<template>
  <div>
    <v-data-table :headers="headers" :items="teams" sort-by="Id" class="elevation-1">
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-spacer></v-spacer>
          <v-dialog v-model="dialog" max-width="500px" v-if="canManageTeams">
            <template v-slot:activator="{ on }">
              <v-btn color="primary" dark class="mb-2" v-on="on">Créer une Équipe</v-btn>
            </template>
            <v-card>
              <v-card-text>
                <v-form ref="teamForm">
                  <v-container>
                    <v-row>
                      <!--Partie de saisie du dialog-->
                      <v-col cols="12" sm="6" md="12">
                        <v-text-field v-model="editedItem.name" label="Nom" :rules="[rules.required, rules.counter]" counter maxlength="60"></v-text-field>
                      </v-col>
                      <v-col></v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDialog">Annuler</v-btn>
                <v-btn color="blue darken-1" text @click="submitForm(editedItem)">{{ isEditing ? "Modifier" : "Créer" }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <v-icon class="mr-2" @click="navigateToTeam(item)" title="Ouvrir la team">mdi-open-in-app</v-icon>
        <v-icon class="mr-2" @click="openTeamDialog(item)" title="Editer" v-if="canManageTeams">edit</v-icon>
        <v-icon @click="deleteTeam(item)" v-if="canManageTeams">delete</v-icon>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { deleteTeamAsync, createTeamAsync, updateTeamAsync, getTeamsByCoachIdAsync } from "@/api/teamApi";
import { getIsManagerPartOfDisciplineAsync, getIsCoachPartOfDisciplineAsync } from "@/api/disciplinesApi";
import { getIsUserPartOfFederationAsync } from "@/api/federationsApi";
import { VDataTable } from "vuetify/lib";
import store from "@/store";
import PermissionsService from "@/services/PermissionsService";
import AuthService from "@/services/AuthService";

export default {
  components: {
    VDataTable,
  },

  props: {
    disciplineId_prop: Number,
    federationId_prop: Number,
    coachId_prop: String,
  },

  data: () => ({
    isCurrentUserPartOfFederation: false,
    isCurrentUserManagerOfDiscipline: false,
    isCurrentUserCoachOfDiscipline: false,
    authService: AuthService,
    dialog: false,
    headers: [
      { text: "Id", value: "id", sortable: true },
      { text: "Nom de l'équipe", value: "name", sortable: true },
      { text: "Nombre d'athlètes", value: "teamUsers.length", sortable: true },
      { text: "Actions", value: "action", sortable: false },
    ],
    isEditing: false,
    teams: [],
    editedItem: {},
    rules: {
      required: (value) => !!value || "Requis.",
      counter: (value) => (value ? value.length <= 60 : "Max 60 caractères"),
    },
  }),

  watch: {
    dialog(val) {
      val || this.closeDialog();
    },
    $route() {
      this.initPage();
    },
  },

  computed: {
    canViewTeams() {
      // Can view own discipline and is manager of current discipline
      let canViewOwnDisciplineAndIsManagerOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
      // Can view own discipline and is coach of current discipline
      let canViewOwnDisciplineAndIsCoachOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserCoachOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsManagerOfCurrentDiscipline || canViewOwnDisciplineAndIsCoachOfCurrentDiscipline || canViewAllDisciplines;
    },
    canManageTeams() {
      // Can manage own discipline and is part of current discipline
      let canManageCurrentDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canManageOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
        // Can manage own Team and is coach of current Team
      let canManageCurrentTeamAndIsPartOfCurrentTeam = PermissionsService.TeamsPermissionsService.canManageOwnTeam && this.isCurrentUserCoachOfDiscipline;
      // Can manage all disciplines
      let canManageAllDisciplines = PermissionsService.DisciplinesPermissionsService.canManageAllDisciplinesOwnFederation;
      return canManageCurrentDisciplineAndIsPartOfCurrentDiscipline || canManageCurrentTeamAndIsPartOfCurrentTeam || canManageAllDisciplines;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : this.federationId_prop;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;
      this.coachId = this.$route.params.coachId ? this.$route.params.coachId : this.coachId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);
      // Check if the current user is part of current discipline
      this.isCurrentUserCoachOfDiscipline = await getIsCoachPartOfDisciplineAsync(this.authService.userId, this.disciplineId);
      // Check if the current user is part of current discipline
      this.isCurrentUserManagerOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);

      // Check if the user can see this page
      if (!this.canViewTeams) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      // Get the list of federation's teams
      this.initializeList();
    },

    // Get the list of federation's teams
    async initializeList() {
      this.teams = await getTeamsByCoachIdAsync(this.coachId);
    },

    // navigate to the team panel
    navigateToTeam(item) {
      this.$router.push({
        name: "TeamPanel",
        params: {
          coachId: this.coachId,
          teamId: item.id,
          tab: "athletes",
        },
      });
    },

    // CREATE or UPDATE a team
    async submitForm(team) {
      // Validate the form
      var isFormValid = this.$refs.teamForm.validate();

      // If the form is valid
      if (isFormValid) {
        team.coachId = this.coachId;
        team.disciplineId = this.disciplineId;
        var result = !this.isEditing ? await createTeamAsync(this.coachId, team) : await updateTeamAsync(team.id, team);

        // If we have an id > 0 in the result, the save succeeded
        if (result && result.id > 0) {
          var createdOrUpdatedMessage = !this.isEditing ? "créée" : "mise à jour";

          store.dispatch("setSnackbar", {
            title: `Team ${createdOrUpdatedMessage}`,
            text: `La team a bien été ${createdOrUpdatedMessage}.`,
            status: "success",
            timeout: 2000,
          });

          this.editedItem = {};
          this.$refs.teamForm.reset();
          this.isEditing = false;
        }

        this.closeDialog();

        await this.initializeList();
      }
      // Else display the error messages
      else {
        // Handle missing name
        if (!team.name) {
          store.dispatch("setSnackbar", {
            title: "Nom requis",
            text: "Veuillez renseigner le nom.",
            status: "error",
            timeout: 2000,
          });
        }
      }
    },

    editTeam(item) {
      this.editedItem = item;
      this.isEditing = true;
      this.dialog = true;
    },

    // Delete a Team
    async deleteTeam(item) {
      if (confirm("Etes-vous sûr de vouloir supprimer cette team ?")) {
        var result = await deleteTeamAsync(item.id);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Team supprimée",
            text: "La team a bien été supprimée.",
            status: "success",
            timeout: 2000,
          });
        }

        await this.initializeList();
      }
    },

    // Open the team dialog
    openTeamDialog(item = null) {
      // If we are in edit mode
      if (item) {
        this.editedItem = item;
        this.isEditing = true;
      } else {
        this.isEditing = false;
      }
      this.dialog = true;
    },

    // Close the team dialog
    closeDialog() {
      this.dialog = false;
    },
  },
};
</script>
